import React, { useState, useEffect } from 'react';
import { 
  Trophy, Crown, Gift, Star, Plus, Edit2, Trash2, Users,
  TrendingUp, Award, Target, Percent, DollarSign, Calendar
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { getImageUrl } from '../../utils/imageUrl';

const RanksRewardsAdmin = () => {
  const [activeTab, setActiveTab] = useState('ranks');
  const [ranks, setRanks] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [offers, setOffers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(null); // 'rank', 'reward', 'offer'
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ranksRes, rewardsRes, offersRes, statsRes] = await Promise.all([
        api.get('/admin/ranks/ranks'),
        api.get('/admin/ranks/rewards'),
        api.get('/admin/ranks/offers'),
        api.get('/admin/ranks/stats')
      ]);
      setRanks(ranksRes.data);
      setRewards(rewardsRes.data);
      setOffers(offersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = async (type, id) => {
    if (!confirm(`Delete this ${type}?`)) return;
    try {
      await api.delete(`/admin/ranks/${type}s/${id}`);
      toast.success(`${type} deleted`);
      fetchData();
    } catch (error) {
      toast.error(`Failed to delete ${type}`);
    }
  };

  const toggleOffer = async (id) => {
    try {
      await api.put(`/admin/ranks/offers/${id}/toggle`);
      toast.success('Offer status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update offer');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="ranks-rewards-admin">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Trophy className="w-8 h-8 text-yellow-500" />
              Ranks & Rewards
            </h1>
            <p className="text-muted-foreground">Manage ranks, rewards, and promotional offers</p>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Crown} label="Total Ranks" value={stats.total_ranks} color="yellow" />
            <StatCard icon={Gift} label="Total Rewards" value={stats.total_rewards} color="pink" />
            <StatCard icon={Star} label="Active Offers" value={stats.active_offers} color="green" />
            <StatCard icon={Award} label="Rewards Granted" value={stats.rewards_granted} color="blue" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b border-white/10 mb-6">
          {['ranks', 'rewards', 'offers'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 capitalize transition-colors ${
                activeTab === tab ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Ranks Tab */}
        {activeTab === 'ranks' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-muted-foreground">{ranks.length} ranks configured</p>
              <button 
                onClick={() => { setShowModal('rank'); setEditingItem(null); }}
                className="btn-primary flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Rank
              </button>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {ranks.map(rank => (
                <div key={rank.id} className="glass-card p-6" style={{ borderColor: rank.color + '40' }}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl">{rank.icon}</span>
                    <div>
                      <h3 className="font-bold text-lg" style={{ color: rank.color }}>{rank.name}</h3>
                      <p className="text-sm text-muted-foreground">Level {rank.level}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-sm mb-4">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Min Referrals</span>
                      <span>{rank.min_referrals}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Min Team Sales</span>
                      <span>₹{rank.min_team_sales}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Monthly Bonus</span>
                      <span className="text-green-500">₹{rank.monthly_bonus}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Commission Boost</span>
                      <span className="text-primary">+{rank.commission_boost}%</span>
                    </div>
                  </div>
                  
                  {rank.benefits?.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs text-muted-foreground mb-1">Benefits:</p>
                      <div className="flex flex-wrap gap-1">
                        {rank.benefits.map((b, i) => (
                          <span key={i} className="px-2 py-0.5 bg-white/5 rounded text-xs">{b}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <button 
                      onClick={() => { setShowModal('rank'); setEditingItem(rank); }}
                      className="flex-1 px-3 py-2 bg-white/5 rounded-lg hover:bg-white/10 text-sm"
                    >
                      <Edit2 className="w-4 h-4 inline mr-1" /> Edit
                    </button>
                    <button 
                      onClick={() => deleteItem('rank', rank.id)}
                      className="px-3 py-2 bg-red-500/20 text-red-500 rounded-lg hover:bg-red-500/30"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Rewards Tab */}
        {activeTab === 'rewards' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-muted-foreground">{rewards.length} rewards configured</p>
              <button 
                onClick={() => { setShowModal('reward'); setEditingItem(null); }}
                className="btn-primary flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Reward
              </button>
            </div>
            
            <div className="glass-card overflow-hidden">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left p-4">Reward</th>
                    <th className="text-left p-4">Type</th>
                    <th className="text-left p-4">Trigger</th>
                    <th className="text-left p-4">Value</th>
                    <th className="text-left p-4">Status</th>
                    <th className="text-left p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rewards.map(reward => (
                    <tr key={reward.id} className="border-t border-white/5">
                      <td className="p-4">
                        <p className="font-medium">{reward.name}</p>
                        <p className="text-xs text-muted-foreground">{reward.description}</p>
                      </td>
                      <td className="p-4">
                        <span className="px-2 py-1 bg-white/10 rounded text-xs capitalize">{reward.type}</span>
                      </td>
                      <td className="p-4">
                        <span className="text-sm">{reward.trigger}: {reward.trigger_value}</span>
                      </td>
                      <td className="p-4">
                        <span className="text-green-500 font-bold">
                          {reward.reward_type === 'cash' ? `₹${reward.reward_value}` : reward.reward_value}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${reward.is_active ? 'bg-green-500/20 text-green-500' : 'bg-gray-500/20'}`}>
                          {reward.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <button 
                            onClick={() => { setShowModal('reward'); setEditingItem(reward); }}
                            className="p-2 hover:bg-white/10 rounded"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => deleteItem('reward', reward.id)}
                            className="p-2 hover:bg-red-500/20 text-red-500 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Offers Tab */}
        {activeTab === 'offers' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-muted-foreground">{offers.length} offers</p>
              <button 
                onClick={() => { setShowModal('offer'); setEditingItem(null); }}
                className="btn-primary flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Create Offer
              </button>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {offers.map(offer => (
                <div key={offer.id} className={`glass-card p-6 ${!offer.is_active ? 'opacity-60' : ''}`}>
                  {offer.banner_image && (
                    <img src={getImageUrl(offer.banner_image)} alt="" className="w-full h-32 object-cover rounded-lg mb-4" />
                  )}
                  <h3 className="font-bold text-lg mb-2">{offer.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{offer.description}</p>
                  
                  <div className="space-y-2 text-sm mb-4">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      {new Date(offer.start_date).toLocaleDateString()} - {new Date(offer.end_date).toLocaleDateString()}
                    </div>
                    <div className="flex items-center gap-2">
                      {offer.bonus_type === 'percentage' ? <Percent className="w-4 h-4" /> : <DollarSign className="w-4 h-4" />}
                      <span className="text-green-500 font-bold">
                        {offer.bonus_type === 'percentage' ? `${offer.bonus_value}%` : `₹${offer.bonus_value}`}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <button 
                      onClick={() => toggleOffer(offer.id)}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm ${offer.is_active ? 'bg-yellow-500/20 text-yellow-500' : 'bg-green-500/20 text-green-500'}`}
                    >
                      {offer.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button 
                      onClick={() => { setShowModal('offer'); setEditingItem(offer); }}
                      className="px-3 py-2 bg-white/5 rounded-lg hover:bg-white/10"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => deleteItem('offer', offer.id)}
                      className="px-3 py-2 bg-red-500/20 text-red-500 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Modals */}
        {showModal === 'rank' && (
          <RankModal 
            rank={editingItem} 
            onClose={() => setShowModal(null)} 
            onSave={() => { setShowModal(null); fetchData(); }} 
          />
        )}
        {showModal === 'reward' && (
          <RewardModal 
            reward={editingItem} 
            onClose={() => setShowModal(null)} 
            onSave={() => { setShowModal(null); fetchData(); }} 
          />
        )}
        {showModal === 'offer' && (
          <OfferModal 
            offer={editingItem} 
            onClose={() => setShowModal(null)} 
            onSave={() => { setShowModal(null); fetchData(); }} 
          />
        )}
      </div>
    </div>
  );
};

// Rank Modal Component
const RankModal = ({ rank, onClose, onSave }) => {
  const [formData, setFormData] = useState(rank || {
    name: '', level: 1, icon: '🏆', color: '#D4AF37',
    min_referrals: 0, min_team_sales: 0, min_personal_sales: 0,
    monthly_bonus: 0, commission_boost: 0, benefits: []
  });
  const [loading, setLoading] = useState(false);
  const [newBenefit, setNewBenefit] = useState('');

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (rank) {
        await api.put(`/admin/ranks/ranks/${rank.id}`, formData);
      } else {
        await api.post('/admin/ranks/ranks', formData);
      }
      toast.success(rank ? 'Rank updated' : 'Rank created');
      onSave();
    } catch (error) {
      toast.error('Failed to save rank');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
      <div className="glass-card p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold mb-6">{rank ? 'Edit Rank' : 'Create Rank'}</h3>
        
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Level</label>
              <input
                type="number"
                value={formData.level}
                onChange={(e) => setFormData({...formData, level: parseInt(e.target.value)})}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Icon</label>
              <input
                type="text"
                value={formData.icon}
                onChange={(e) => setFormData({...formData, icon: e.target.value})}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Color</label>
              <input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({...formData, color: e.target.value})}
                className="w-full h-10 rounded cursor-pointer"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Min Referrals</label>
              <input
                type="number"
                value={formData.min_referrals}
                onChange={(e) => setFormData({...formData, min_referrals: parseInt(e.target.value)})}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Min Team Sales</label>
              <input
                type="number"
                value={formData.min_team_sales}
                onChange={(e) => setFormData({...formData, min_team_sales: parseFloat(e.target.value)})}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Min Personal Sales</label>
              <input
                type="number"
                value={formData.min_personal_sales}
                onChange={(e) => setFormData({...formData, min_personal_sales: parseFloat(e.target.value)})}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Monthly Bonus (₹)</label>
              <input
                type="number"
                value={formData.monthly_bonus}
                onChange={(e) => setFormData({...formData, monthly_bonus: parseFloat(e.target.value)})}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Commission Boost (%)</label>
              <input
                type="number"
                value={formData.commission_boost}
                onChange={(e) => setFormData({...formData, commission_boost: parseFloat(e.target.value)})}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Benefits</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newBenefit}
                onChange={(e) => setNewBenefit(e.target.value)}
                className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                placeholder="Add benefit..."
              />
              <button
                onClick={() => {
                  if (newBenefit) {
                    setFormData({...formData, benefits: [...formData.benefits, newBenefit]});
                    setNewBenefit('');
                  }
                }}
                className="px-4 py-2 bg-primary/20 text-primary rounded-lg"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.benefits.map((b, i) => (
                <span key={i} className="px-2 py-1 bg-white/10 rounded text-sm flex items-center gap-1">
                  {b}
                  <button onClick={() => setFormData({...formData, benefits: formData.benefits.filter((_, idx) => idx !== i)})} className="text-red-500">×</button>
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={handleSubmit} disabled={loading} className="flex-1 btn-primary disabled:opacity-50">
            {loading ? 'Saving...' : 'Save Rank'}
          </button>
          <button onClick={onClose} className="flex-1 px-4 py-2 bg-white/5 rounded-full">Cancel</button>
        </div>
      </div>
    </div>
  );
};

// Reward Modal (simplified)
const RewardModal = ({ reward, onClose, onSave }) => {
  const [formData, setFormData] = useState(reward || {
    name: '', description: '', type: 'one_time', trigger: 'rank_achieved',
    trigger_value: '', reward_type: 'cash', reward_value: 0, is_active: true
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (reward) {
        await api.put(`/admin/ranks/rewards/${reward.id}`, formData);
      } else {
        await api.post('/admin/ranks/rewards', formData);
      }
      toast.success(reward ? 'Reward updated' : 'Reward created');
      onSave();
    } catch (error) {
      toast.error('Failed to save reward');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
      <div className="glass-card p-6 max-w-lg w-full">
        <h3 className="text-xl font-bold mb-6">{reward ? 'Edit Reward' : 'Create Reward'}</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Name</label>
            <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Description</label>
            <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg" rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Trigger</label>
              <select value={formData.trigger} onChange={(e) => setFormData({...formData, trigger: e.target.value})}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg">
                <option value="rank_achieved">Rank Achieved</option>
                <option value="sales_target">Sales Target</option>
                <option value="referral_count">Referral Count</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Trigger Value</label>
              <input type="text" value={formData.trigger_value} onChange={(e) => setFormData({...formData, trigger_value: e.target.value})}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg" placeholder="e.g., Gold or 10000" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Reward Type</label>
              <select value={formData.reward_type} onChange={(e) => setFormData({...formData, reward_type: e.target.value})}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg">
                <option value="cash">Cash</option>
                <option value="product">Product</option>
                <option value="badge">Badge</option>
                <option value="coupon">Coupon</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Value</label>
              <input type="number" value={formData.reward_value} onChange={(e) => setFormData({...formData, reward_value: parseFloat(e.target.value)})}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg" />
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={handleSubmit} disabled={loading} className="flex-1 btn-primary disabled:opacity-50">
            {loading ? 'Saving...' : 'Save Reward'}
          </button>
          <button onClick={onClose} className="flex-1 px-4 py-2 bg-white/5 rounded-full">Cancel</button>
        </div>
      </div>
    </div>
  );
};

// Offer Modal (simplified)
const OfferModal = ({ offer, onClose, onSave }) => {
  const [formData, setFormData] = useState(offer || {
    title: '', description: '', type: 'bonus', eligibility: 'all',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
    bonus_type: 'percentage', bonus_value: 0, is_active: true
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (offer) {
        await api.put(`/admin/ranks/offers/${offer.id}`, formData);
      } else {
        await api.post('/admin/ranks/offers', formData);
      }
      toast.success(offer ? 'Offer updated' : 'Offer created');
      onSave();
    } catch (error) {
      toast.error('Failed to save offer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
      <div className="glass-card p-6 max-w-lg w-full">
        <h3 className="text-xl font-bold mb-6">{offer ? 'Edit Offer' : 'Create Offer'}</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Title</label>
            <input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Description</label>
            <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg" rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Start Date</label>
              <input type="date" value={formData.start_date?.split('T')[0]} onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">End Date</label>
              <input type="date" value={formData.end_date?.split('T')[0]} onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Bonus Type</label>
              <select value={formData.bonus_type} onChange={(e) => setFormData({...formData, bonus_type: e.target.value})}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg">
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Bonus Value</label>
              <input type="number" value={formData.bonus_value} onChange={(e) => setFormData({...formData, bonus_value: parseFloat(e.target.value)})}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg" />
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={handleSubmit} disabled={loading} className="flex-1 btn-primary disabled:opacity-50">
            {loading ? 'Saving...' : 'Save Offer'}
          </button>
          <button onClick={onClose} className="flex-1 px-4 py-2 bg-white/5 rounded-full">Cancel</button>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => {
  const colorClasses = {
    yellow: 'text-yellow-500 bg-yellow-500/20',
    pink: 'text-pink-500 bg-pink-500/20',
    green: 'text-green-500 bg-green-500/20',
    blue: 'text-blue-500 bg-blue-500/20'
  };

  return (
    <div className="glass-card p-4">
      <div className="flex items-center gap-3">
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm text-muted-foreground">{label}</p>
        </div>
      </div>
    </div>
  );
};

export default RanksRewardsAdmin;
